import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { map } from 'rxjs';

import { AuthService } from './services/auth/auth.service';
import { authGuard } from './guards/auth.guard';
import { guestGuard } from './guards/guest.guard';
import { adminGuard } from './guards/admin.guard';
import { globalAdminGuard } from './guards/global-admin.guard';
import { teamGuard } from './guards/team.guard';

export const routes: Routes = [
  // ── Public / guest-only pages ────────────────────────────────────────────
  // Already logged in → redirect to /dashboard
  {
    path: 'login',
    title: 'Sign in',
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/login/login').then((m) => m.LoginPage),
  },
  {
    path: 'login/password-forgotten',
    title: 'Forgot password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/login/password-forgotten/password-forgotten').then(
        (m) => m.PasswordForgottenPage,
      ),
  },
  {
    path: 'login/reset-password',
    title: 'Reset password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/login/reset-password/reset-password').then((m) => m.ResetPasswordPage),
  },
  // No guard: the link must also work while someone else is signed in.
  {
    path: 'login/mfa-reset',
    title: 'Reset MFA',
    loadComponent: () => import('./pages/login/mfa-reset/mfa-reset').then((m) => m.MfaResetPage),
  },
  // ── App shell (sidebar + header) — requires active session ──────────────
  {
    path: '',
    canActivate: [authGuard],
    // Re-run guards on every child navigation so the MFA-enforcement redirect
    // in authGuard fires even when navigating between sibling child routes.
    runGuardsAndResolvers: 'always',
    loadComponent: () =>
      import('./layouts/dashboard-layout/dashboard-layout').then((m) => m.DashboardLayout),
    children: [
      // Redirect bare root URL to the landing page for the user's role:
      // admin/reseller → Clients overview, customer → dashboard. The session
      // check runs first because the user is not loaded yet when the redirect
      // function executes (redirects run before route guards).
      {
        path: '',
        pathMatch: 'full',
        redirectTo: () => {
          const auth = inject(AuthService);
          return auth.isLoggedIn().pipe(
            map(() => (auth.isAdminArea() ? '/admin' : '/dashboard')),
          );
        },
      },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard').then((m) => m.DashboardPage),
      },
      {
        path: 'account',
        title: 'Account',
        loadComponent: () => import('./pages/account/account').then((m) => m.AccountPage),
      },
      {
        path: 'notifications',
        title: 'Notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications').then((m) => m.NotificationsPage),
      },
      {
        path: 'security',
        title: 'Security',
        loadComponent: () => import('./pages/security/security').then((m) => m.SecurityPage),
      },
      {
        path: 'backups',
        title: 'Backups',
        loadComponent: () => import('./pages/backups/backups').then((m) => m.BackupsPage),
      },
      {
        path: 'backups/:id',
        title: 'Backup details',
        loadComponent: () =>
          import('./pages/backup-detail/backup-detail').then((m) => m.BackupDetailPage),
      },
      {
        path: 'documents',
        title: 'Documents',
        loadComponent: () => import('./pages/contracts/contracts').then((m) => m.ContractsPage),
      },
      {
        path: 'activity-log',
        title: 'Activity log',
        loadComponent: () =>
          import('./pages/activity-log/activity-log').then((m) => m.ActivityLogPage),
      },
      {
        path: 'company',
        title: 'Company',
        loadComponent: () => import('./pages/company/company').then((m) => m.CompanyPage),
      },
      {
        path: 'users',
        title: 'Users',
        canActivate: [teamGuard],
        loadComponent: () => import('./pages/team/team').then((m) => m.TeamPage),
      },
      {
        path: 'infrastructure',
        title: 'Infrastructure',
        loadComponent: () =>
          import('./pages/infrastructure/infrastructure').then((m) => m.InfrastructurePage),
      },
      {
        path: 'admin',
        title: 'Clients',
        canActivate: [adminGuard],
        loadComponent: () => import('./pages/admin/admin').then((m) => m.AdminPage),
      },
      {
        path: 'admin/resellers',
        title: 'Resellers',
        canActivate: [globalAdminGuard],
        loadComponent: () =>
          import('./pages/admin/resellers/resellers').then((m) => m.ResellersPage),
      },
      {
        path: 'admin/resellers/:id',
        title: 'Reseller',
        canActivate: [globalAdminGuard],
        loadComponent: () =>
          import('./pages/admin/reseller-detail/reseller-detail').then((m) => m.ResellerDetailPage),
      },
      {
        path: 'admin/admins',
        title: 'Admins',
        canActivate: [globalAdminGuard],
        loadComponent: () => import('./pages/admin/admins/admins').then((m) => m.AdminsPage),
      },
      {
        path: 'admin/tenants/:id',
        title: 'Client',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/admin/tenant-detail/tenant-detail').then((m) => m.TenantDetailPage),
      },
      {
        path: 'admin/tenants/:id/runs/:runId',
        title: 'Backup run',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/admin/run-detail/run-detail').then((m) => m.RunDetailPage),
      },
      {
        path: 'admin/tenants/:id/jobs/:jobId',
        title: 'Backup job',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/admin/job-detail/job-detail').then((m) => m.JobDetailPage),
      },
      {
        path: 'admin/users/:id',
        title: 'User',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./pages/admin/user-detail/user-detail').then((m) => m.AdminUserDetailPage),
      },
    ],
  },

  // ── Fallback ─────────────────────────────────────────────────────────────
  { path: '**', redirectTo: 'login' },
];
